import React, { PureComponent, Fragment } from 'react';
import { connect } from 'react-redux';
import { withTranslation } from 'react-i18next';
import PropTypes from 'prop-types';
import _ from 'lodash';
import ReCAPTCHA from 'react-google-recaptcha';
import { format } from 'date-fns';
import {
  TextField,
  Stepper,
  Step,
  StepButton,
  MenuItem,
  FormHelperText,
  FormControlLabel,
  Checkbox,
  InputAdornment,
  LinearProgress,
  InputLabel
} from '@material-ui/core';
import { DatePicker } from '@material-ui/pickers';
import classNames from 'classnames';

import './Form.scss';
import Icon from '../Icon/Icon';
import Button from '../Button/Button';
import { isMobile } from 'react-device-detect';

class Form extends PureComponent {

  constructor(props) {
    super(props);

    this.state = {
      title: '1',
      firstname: '',
      lastname: '',
      street: '',
      postalCode: '',
      city: '',
      country: props.countryByIP,
      province: '',
      securityQuestion: '',
      answer: '',
      birthdate: '',
      username: '',
      password: '',
      confirmPassword: '',
      email: '',
      confirmEmail: '',
      idNumber: '',
      phoneNumber: '',
      phoneNumberConfirmation: '',
      subscribeProducts: false,
      subscribeNewsletter: false,
      over18: false,
      remember: false,
      activeStep: 0,
      stepAction: '',
      showPassword: false,
      till: '',
      errors: {
        firstname: '',
        lastname: '',
        street: '',
        postalCode: '',
        city: '',
        province: '',
        securityQuestion: '',
        answer: '',
        birthdate: '',
        username: '',
        password: '',
        confirmPassword: '',
        email: '',
        confirmEmail: '',
        idNumber: '',
        phoneNumber: '',
        phoneNumberConfirmation: '',
        over18: ''
      }
    };

    if (props.countryByIP) {
      this.props.getProvinces(props.countryByIP);
    }
  }

  componentDidUpdate() {
    if (!this.props.steps[this.state.activeStep]) {
      this.setState({ activeStep: this.props.steps.length -1 });
    }
  }

  compileFormInfo = () => {
    // Get form name to know which function to call.
    const funcName = _.replace(this.containerRef.dataset.container, '-form', '');

    // Get form inputs to know which state keys to send.
    const inputs = _.reduce(this.props.steps, (result, step) => {
      const stepInput = step.inputs && _.filter(step.inputs, input => input !== undefined);

      return stepInput ? [...result, ...stepInput] : result;
    }, []);

    // Pick state for desired keys according to current form.
    const obj = _.reduce(inputs, (result, input) => {
      return { ...result, ...{ [input.name]: this.state[input.name] } };
    }, {});

    return { funcName, inputs, obj };
  }

  onChange = (e) => {
    const { name, type, checked, value } = e.target;
    const { inputs } = this.compileFormInfo();

    this.setState({
      [name]: type === 'checkbox' ? checked : value
    }, async () => {
      this.validateForm(null, name)(e);

      const input = inputs.filter(i => i.name === name)[0];
      if (name === 'password' && (!!input.rules && input.rules.strength)) {
        this.validatePasswordStrength(value);
      }

      if (name === 'phoneNumber'){
        this.validatePhone(value);
      }

      if (input.rules?.validate === 'email') {
        this.validateEmail(value);
      }

    });
  }

  onDateChange = (name) => (value) => {
    const { inputs } = this.compileFormInfo();

    this.setState({
      [name]: format(value, 'yyyy-MM-dd')
    }, async () => {
      this.validateForm(null, name)();

      const input = inputs.filter(i => i.name === name)[0];
      if (name === 'password' && (!!input.rules && input.rules.strength)) {
        this.validatePasswordStrength(value);
      }
    });
  }

  onRecaptchChange = () => {
    const { funcName, obj } = this.compileFormInfo();

    this.props[funcName] ? this.props[funcName](obj) : this.props.close();
  }

  inputDateFocus = (e) => {
    e.currentTarget.type = 'date';
  }

  inputDateBlur = (e) => {
    e.currentTarget.type = 'text';
  }

  setErrorMessage = (input) => {
    const { t, passwordValidation, emailStatus } = this.props;
    const { rules, label, name } = input;
    const value = this.state[name];
    let message = '';

    if (!rules) {
      return message;
    }

    if (!!rules.pattern && !rules.pattern.test(value)) {
      if ((input.name !== 'email' && !input.rules.isRetail) && value.length > 0){
        message = `${ t('form.invalidBlank') } ${ label }`;
      }
    }

    if (!!rules.match && value !== this.state[rules.match]) {
      message = t('form.fieldsDontMatch');
    }

    if (!!rules.strength && passwordValidation && passwordValidation.strength < rules.strength) {
      message = `${ label } ${ t('form.blankTooWeak') }`;
    }

    if (!!rules.min && value.length < rules.min) {
      message = `${ label } ${ t('form.blankTooShort') }`;
    }

    if (!!rules.max && value.length > rules.max) {
      message = `${ label } ${ t('form.blankTooLong') }`;
    }

    // Using a year of 365.25 days (0.25 because of leap years) which are 3.15576e+10 milliseconds (365.25 * 24 * 60 * 60 * 1000) respectively.
    if (!!rules.over && Math.floor((new Date() - new Date(value).getTime()) / 3.15576e+10) < rules.over) {
      message = `${ t('form.overBlank') } ${ rules.over }`;
    }

    if (!!rules.required && (value.length <= 0 || !value) && input.name !== 'email' &&
    (input.name !== 'phoneNumber' && input.name !== 'phoneNumberConfirmation')) {
      message = t('form.requiredField');
    }

    if (input.name === 'email' && !!rules.required && (value.length <= 0 || !value) && !input.rules.isRetail && this.checkInputRules(input)) {
      message = t('form.requiredField');
    }

    if ((input.name === 'phoneNumber' || input.name === 'phoneNumberConfirmation') && !!rules.required && (value.length <= 0 || !value)){
      if (this.state.email === ''){
        message = t('form.requiredField');
      }
    }

    if (rules?.validate === 'email' && emailStatus === 'not_available') {
      message = t('form.emailInUse');
    }

    if (rules?.validate === 'phone' && this.props.phoneStatus === 'not_available'){
      message = t('form.phoneInUse');
    }

    return message;
  }

  validateForm = (action, inputName = null) => (e) => {
    if (e) {
      e.persist();
    }

    const errors = action ? {} : JSON.parse(JSON.stringify(this.state)).errors;
    const inputs = this.props.steps[this.state.activeStep].inputs;

    if (inputs) {
      for (const input of inputs) {
        if (!action) {
          if (input.name === inputName) {
            errors[input.name] = input.rules ? this.setErrorMessage(input) : '';
          }
        } else {
          if (input.name) {
            errors[input.name] = input.rules ? this.setErrorMessage(input) : '';
          }
        }
      }
    }

    this.setState({ errors }, () => {
      if (action) {
        let valid = true;
        for (const err of Object.values(this.state.errors)) {
          err.length > 0 && (valid = false);
        }

        if (valid) {
          this.changeStep(action)(e);
        }
      }
    });
  }

  validatePasswordStrength = _.debounce(async (value) => {
    await this.props.validatePassword(value);
    this.validateForm(null, 'password')();
  }, 500, { maxWait: 1000 })

  validateEmail = _.debounce(async (value) => {
    await this.props.checkEmail(value);
    this.validateForm(null, 'email')();
  }, 500, { maxWait: 1000 })

  validatePhone = _.debounce(async (value) => {
    await this.props.checkPhone(value);
    this.validateForm(null, 'phoneNumber')();
  }, 500, { maxWait: 1000 })

  changeStep = (action) => (e) => {
    e.preventDefault();

    const { activeStep } = this.state;
    const { funcName, obj } = this.compileFormInfo();

    if (action === 'prev') {
      this.setState({ stepAction: 'prev', activeStep: activeStep - 1 });
    } else {
      if (this.props.steps.length > 1) {
        if (activeStep === this.props.steps.length - 1) {
          if (this.recaptchaRef) {
            this.recaptchaRef.execute();
          } else {
            this.props[funcName] ? this.props[funcName](obj) : this.props.close();
          }
        } else {
          this.setState({ activeStep: activeStep + 1 });
        }
      } else {
        if (this.recaptchaRef) {
          this.recaptchaRef.execute();
        } else {
          this.props[funcName] ? this.props[funcName](obj) : this.props.close();
          if (funcName === 'confirmationMessage' || funcName === 'register')
            this.props.close();
        }
      }
    }
  }

  onSubmit = async (e) => {
    e.preventDefault();

    await this.validateForm(true)(e);
  }

  buildStepper = (steps) => {
    const { activeStep } = this.state;

    return (
      <Stepper
        alternativeLabel
        activeStep={ activeStep }
        className="form-container__stepper"
      >
        {
          steps && _.map(steps, (step, i) => {
            return (
              <Step key={ step.label } completed={ activeStep > i || activeStep === i }>
                <StepButton disabled>{ i + 1 }</StepButton>
              </Step>
            );
          })
        }
      </Stepper>
    );
  }

  checkInputRules = (input) => {
    const { phoneNumber } = this.state;

    if (input.name === 'email' && phoneNumber !== ''){
      return false;
    }

    return input.rules && input.rules.required;
  };

  buildStepDetail = (input, index) => {
    const {
      icon,
      empty,
      hidden,
      type,
      name,
      rules,
      label,
      className,
      items,
      multiline,
      placeholder,
      onSelect,
      link,
      hideIcon,
      horizontal,
      text,
      autoFocus
    } = input;
    const {
      t,
      passwordValidation: { strength, tips },
      passwordStrength: { minLength, maxLength, minStrength }
    } = this.props;
    const { errors, showPassword } = this.state;
    const strengthLevel = minStrength > strength ? 0 : (strength * 20 || 0);

    const passwordValidationClauses = {
      uppercase_letters: { msg: t('form.uppercase_letters'), valid: false },
      lowercase_letters: { msg: t('form.lowercase_letters'), valid: false },
      numbers: { msg: t('form.numbers'), valid: false },
      special_chars: { msg: t('form.special_chars'), valid: false },
      long: { msg: t('form.long'), valid: false }
    };

    if (!input || _.isEmpty(input)) {
      return;
    }

    for (const clause of Object.keys(passwordValidationClauses)) {
      passwordValidationClauses[clause].valid = tips.indexOf(clause) === -1;
    }

    let passwordStrengthLabel = 'Very Weak';
    let passwordStrengthClass = 'red';
    if (strengthLevel >= 100) {
      passwordStrengthLabel = 'Very Strong';
      passwordStrengthClass = 'green';
    } else if (strengthLevel >= 80) {
      passwordStrengthLabel = 'Strong';
      passwordStrengthClass = 'green';
    } else if (strengthLevel >= 60) {
      passwordStrengthLabel = 'Good';
      passwordStrengthClass = 'yellow';
    } else if (strengthLevel >= 40) {
      passwordStrengthLabel = 'Weak';
    }

    if (empty === true) {
      return <div key={ index } className="form-container__form-control"></div>;
    }

    if (hidden === true) {
      return <div key={ index } className="form-container__form-control form-container__form-control--hidden"></div>;
    }

    if (!_.isEmpty(input.value)){
      this.setState({ [name]: input.value });
    }

    if (type === 'label') {
      return (
        <div
          key={ index }
          className={
            classNames({
              'form-container__form-control': true,
              [`form-container__form-control--${ className }`]: className
            })
          }
        >
          <label className="form-container__form-label">{ text }</label>
        </div>
      );
    }

    if (type === 'phone') {
      return (
        <div key={ index } className="form-container__form-control">
          <InputLabel className="form-container__form-input--label">
            { label } { (rules && rules.required) && this.state.email === '' ? '*' : '' }
          </InputLabel>
          <TextField
            type="tel"
            placeholder={ placeholder }
            name={ name }
            autoComplete="new-password"
            className="form-container__form-input"
            value={ this.state[name] }
            onChange={ this.onChange }
            multiline={ !!multiline }
            rows={ multiline }
            variant="outlined"
            error={ !!errors[name] }
            helperText={ errors[name] }
            required={ rules && rules.required && this.state.email === '' }
            InputLabelProps={ { shrink: false } }
            InputProps={
              {
                startAdornment: (
                  icon && <InputAdornment position="start">
                    { <Icon icon={ icon } /> }
                  </InputAdornment>
                )
              }
            }
          />
        </div>
      );
    }

    if (type === 'checkbox') {
      return (
        <Fragment key={ index }>
          <br />
          <FormControlLabel
            className={ classNames({ 'form-container__form-control--error': !!errors[name] }) }
            control={
              <Checkbox
                name={ name }
                checked={ this.state[name] }
                onChange={ this.onChange }
              />
            }
            label={
              !link ?
                <span className="form-container__form-control--color">{ label }</span>
                :
                <a href={ link } target="_blank" rel="noopener noreferrer">{ label + '*' }</a>
            }
          />
          <FormHelperText className="form-container__form-control--error">{ errors[name] }</FormHelperText>
        </Fragment>
      );
    }

    if (type === 'select') {
      return (
        <div
          key={ index }
          className={
            classNames({
              'form-container__form-control': true,
              [`form-container__form-control--${ className }`]: className
            })
          }
        >
          <InputLabel className="form-container__form-input--label">
            { label } { rules && rules.required ? '*' : '' }
          </InputLabel>
          <TextField
            select
            placeholder={ placeholder }
            name={ name }
            autoComplete="new-password"
            className="form-container__form-input"
            value={ this.state[name] }
            onChange={ (e) => { this.onChange(e); onSelect && this.props[onSelect](e.target.value); } }
            variant="outlined"
            error={ !!errors[name] }
            helperText={ errors[name] }
            required={ rules && rules.required }
            disabled={ rules && !!rules.dependency && !this.state[rules.dependency] }
            InputLabelProps={ { shrink: false } }
            InputProps={
              {
                startAdornment: (
                  icon && <InputAdornment position="start">
                    { <Icon icon={ icon } /> }
                  </InputAdornment>
                )
              }
            }
          >
            {
              items && items.map((item, i) =>
                <MenuItem key={ i } value={ item.value }>{ item.text }</MenuItem>
              )
            }
          </TextField>
        </div>
      );
    }

    if (type === 'date') {
      return (
        <div
          key={ index }
          className={
            classNames({
              'form-container__form-control': true,
              [`form-container__form-control--${ className }`]: className
            })
          }
        >
          <InputLabel className="form-container__form-input--label">
            { label } { rules && rules.required ? '*' : '' }
          </InputLabel>
          <DatePicker
            disableFuture
            openTo="year"
            format="yyyy-MM-dd"
            views={ ['year', 'month', 'date'] }
            name={ name }
            className="form-container__form-input"
            value={ this.state[name] ? this.state[name] : null }
            onChange={ this.onDateChange(name) }
            inputVariant="outlined"
            TextFieldComponent={ TextField }
            InputLabelProps={ { shrink: false } }
            error={ !!errors[name] }
            helperText={ errors[name] }
            required={ rules && rules.required }
          />
        </div>
      );
    }

    if (type === 'password') {
      const { activeStep } = this.state;
      const { steps } = this.props;
      const step = steps[activeStep];
      const password = _.find(step.inputs, { name: 'password' });

      return (
        <>
          <div
            key={ index }
            className={
              classNames({
                'form-container__form-control': true,
                [`form-container__form-control--${ className }`]: className,
                'form-container__form-control--horizontal': horizontal
              })
            }
          >
            <InputLabel className="form-container__form-input--label">
              { label } { rules && rules.required ? '*' : '' }
            </InputLabel>
            <TextField
              type={ showPassword ? 'text' : 'password' }
              placeholder={ placeholder }
              name={ name }
              className="form-container__form-input"
              value={ this.state[name] }
              onChange={ this.onChange }
              autoFocus={ autoFocus }
              multiline={ !!multiline }
              rows={ multiline }
              variant="outlined"
              error={ !!errors[name] }
              helperText={ name !== 'password' && errors[name] }
              required={ rules && rules.required }
              InputLabelProps={ { shrink: false } }
              InputProps={
                {
                  startAdornment: (
                    icon && <InputAdornment position="start">
                      { <Icon icon={ icon } /> }
                    </InputAdornment>
                  ),
                  endAdornment: (
                    !hideIcon && (
                      <InputAdornment position="end" className="form-container__eye-icon">
                        {
                          <Icon
                            icon={ showPassword ? 'eyeOpen' : 'eyeClosed' }
                            className="form-container__eye-icon"
                            onClick={ () => this.setState({ showPassword: !showPassword }) }
                          />
                        }
                      </InputAdornment>
                    )
                  )
                }
              }
            />
            {
              this.state[name] && (rules && rules.strength && !input.isRetail) && (
                <FormHelperText
                  component="div"
                  className={
                    `form-container__form-strength form-container__form-strength--${ passwordStrengthClass }`
                  }
                >
                  <span>{ passwordStrengthLabel }</span>
                  <LinearProgress variant="determinate" value={ strengthLevel } />
                </FormHelperText>
              )
            }
            {
              name === 'password' && this.state[name] && (rules && rules.strength) && (
                <FormHelperText component="div" className="form-container__password-hints">
                  {
                    ((input.isRetail && this.state[name].length >=1 && this.state[name].length < 4) ||
                  (input.isRetail && this.state[name].length > 6)) && (
                      <h5 className="form-container____password-hints not-valid">
                        { t('form.retailPasswordPolicyTitle').replace('%min', rules.min).replace('%max', rules.max) }
                      </h5>
                    )
                  }
                  {
                    ((!input.isRetail && minStrength < 5) && !isMobile)&& (
                      <h5>{
                        t('form.passwordPolicyTitle').replace('%min',
                          minLength).replace('%max', maxLength).replace('%strength', minStrength)
                      }</h5>
                    )
                  }
                  {
                    !isMobile &&
                    <>
                      {
                        (!input.isRetail && minStrength === 5) && (
                          <h5>{ t('form.passwordPolicyTitleStrict') }</h5>
                        )
                      }
                      {
                        !input.isRetail &&
                    _.map(Object.keys(passwordValidationClauses), (tip, index) => (
                      <p
                        key={ index }
                        className={
                          classNames({
                            'form-container____password-hints': true,
                            'valid': passwordValidationClauses[tip].valid,
                            'not-valid': !passwordValidationClauses[tip].valid
                          })
                        }
                      >
                        { passwordValidationClauses[tip].msg }
                      </p>
                    ))
                      }
                    </>
                  }
                </FormHelperText>
              )
            }
          </div>
          {
            ((type === 'password' && name !== 'password') && isMobile) && <div
              key={ index }
              className={
                classNames({
                  'form-container__form-control form-container__form-control--ful-width': true,
                  [`form-container__form-control--${ className }`]: className,
                  'form-container__form-control--horizontal': horizontal
                })
              }
            >
              {
                this.state['password'] && (password.rules && password.rules.strength) && (
                  <FormHelperText component="div" className="form-container__password-hints">
                    {
                      ((this.state['password'].length >=1 && this.state['password'].length < 4) ||
                  (this.state['password'].length > 6)) && (
                        <h5 className="form-container____password-hints not-valid">
                          { t('form.retailPasswordPolicyTitle').replace('%min', password.rules.min).replace('%max', password.rules.max) }
                        </h5>
                      )
                    }
                    {
                      ((password.minStrength < 5) && isMobile)&& (
                        <h5>{
                          t('form.passwordPolicyTitle').replace('%min',
                            password.minLength).replace('%max', password.maxLength).replace('%strength', password.minStrength)
                        }</h5>
                      )
                    }

                    {
                      (password.minStrength === 5) && (
                        <h5>{ t('form.passwordPolicyTitleStrict') }</h5>
                      )
                    }
                    {
                      !input.isRetail &&
                  _.map(Object.keys(passwordValidationClauses), (tip, index) => (
                    <p
                      key={ index }
                      className={
                        classNames({
                          'form-container____password-hints': true,
                          'valid': passwordValidationClauses[tip].valid,
                          'not-valid': !passwordValidationClauses[tip].valid
                        })
                      }
                    >
                      { passwordValidationClauses[tip].msg }
                    </p>
                  ))
                    }

                  </FormHelperText>
                )
              }
            </div>
          }
        </>
      );
    }

    if (type === 'recaptcha') {
      return (
        <div key={ index } className="form-container__form-control form-container__form-control--full-width form-container__form-recaptcha">
          <ReCAPTCHA
            ref={ el => this.recaptchaRef = el }
            size="invisible"
            badge="inline"
            hl={ this.props.selectedLanguage }
            sitekey={ process.env.REACT_APP_RECAPTCHA_TOKEN }
            onChange={ this.onRecaptchChange }
          />
        </div>
      );
    }

    return (
      <div
        key={ index }
        className={
          classNames({
            'form-container__form-control': true,
            [`form-container__form-control--${ className }`]: className,
            'form-container__form-control--horizontal': horizontal
          })
        }
      >
        <InputLabel className="form-container__form-input--label">
          { label } { this.checkInputRules(input) ? '*' : '' }
        </InputLabel>
        <TextField
          type={ type }
          placeholder={ placeholder }
          name={ name }
          autoComplete="new-password"
          className="form-container__form-input"
          disabled= { input.disabled }
          value={ this.state[name] }
          onChange={ this.onChange }
          multiline={ !!multiline }
          rows={ multiline }
          variant="outlined"
          error={ !!errors[name] }
          helperText={ errors[name] }
          required={ this.checkInputRules(input) }
          InputProps={
            {
              startAdornment: (
                icon && <InputAdornment position="start">
                  { <Icon icon={ icon } /> }
                </InputAdornment>
              )
            }
          }
        />
      </div>
    );
  }

  buildButton = (button, index) => {
    const { icon, type, label, action, className } = button;
    const futureAction = action || 'next';

    if (type === 'submit') {
      return (
        <Button
          key={ type }
          className={
            classNames({
              'btn--cta btn--big': true,
              [`btn--${ className }`]: className
            })
          }
          text={ icon ? `${ <Icon icon={ icon } /> } ${ label }` : label }
          type="submit"
        />

      );
    }

    return (
      <Button
        key={ index }
        variant="contained"
        className={ `btn--${ type }` }
        size="large"
        onClick={ futureAction === 'next' ? this.validateForm(futureAction) : this.changeStep(futureAction) }
        type="button"
      >
        { icon && <Icon icon={ icon } /> }
      </Button>
    );
  }

  buildLink = (link, index) => {
    const { text, action, hasButton, decoration = false } = link;

    if (!action) {
      return (
        <Button
          key={ index }
          variant="contained"
          className="form-container__form-button form-container__form-button--link"
          type="submit"
        >
          {
            !hasButton ? text :
              (
                <>
                  { text.substring(0, text.lastIndexOf(' ')) }
                  <span className="form-container__form-links--primary">{ text.split(' ').splice(-1) }</span></>
              )
          }
        </Button>
      );
    }

    return (
      <a key={ index } href={ `#${ action }` } onClick={ this.props.close }>
        {
          !hasButton ?
            <span className={ decoration ? 'form-container__form-links--decoration' : '' }>{ text }</span>
            :
            (
              <>
                { text.substring(0, text.lastIndexOf(' ')) }
                <span className="form-container__form-links--primary">{ text.split(' ').splice(-1) }</span>
              </>
            )
        }
      </a>
    );
  }

  render() {
    const { activeStep } = this.state;
    const { steps, containerClass, title } = this.props;
    const step = steps[activeStep];

    return (
      <div
        ref={ el => this.containerRef = el }
        className={
          classNames({
            'form-container': true,
            [`form-container--${ containerClass }`]: containerClass
          })
        }
        data-container={ containerClass }
      >
        {
          step && (
            <>
              <div className="form-container__tab-title">{ title || step.title }</div>

              { steps.length > 1 && this.buildStepper(steps) }
              <form ref={ el => this.formRef = el } onSubmit={ this.onSubmit } noValidate>
                {
                  steps &&
                    <>
                      {
                        step.inputs &&
                          <div className="form-container__form-inputs">
                            { _.map(step.inputs, this.buildStepDetail) }
                          </div>
                      }

                      {
                        step.span &&
                          _.map(step.span, (text, index) => <span key={ index } className="form-container__form-instructions">{ text }</span>)
                      }
                      <div className="form-container__form-buttons">
                        { step.buttons && _.map(step.buttons, this.buildButton) }
                      </div>

                      {
                        step.links &&
                          <div className="form-container__form-links">
                            { _.map(step.links, this.buildLink) }
                          </div>
                      }
                    </>
                }
              </form>
            </>
          )
        }
      </div>
    );
  }

}

Form.propTypes = {
  t: PropTypes.func,
  containerClass: PropTypes.string.isRequired,
  title: PropTypes.string,
  steps: PropTypes.array.isRequired,
  selectedLanguage: PropTypes.string,
  close: PropTypes.func,
  passwordValidation: PropTypes.object,
  passwordStrength: PropTypes.object,
  validatePassword: PropTypes.func,
  countryByIP: PropTypes.string,
  getProvinces: PropTypes.func,
  checkEmail: PropTypes.func,
  checkPhone: PropTypes.func,
  emailStatus: PropTypes.string,
  phoneStatus: PropTypes.string
};

const mapState = state => ({
  selectedLanguage: state.odds.selectedLanguage,
  passwordValidation: state.content.passwordValidation,
  passwordStrength: state.content.passwordStrength,
  countryByIP: state.content.countryByIP,
  emailStatus: state.auth.emailStatus,
  phoneStatus: state.auth.phoneStatus
});

const mapDispatch = dispatch => ({
  login: (obj) => dispatch.auth.login(obj),
  loginNoRedirect: (obj) => dispatch.auth.loginNoRedirect(obj),
  register: (obj) => dispatch.auth.register(obj),
  forgotPassword: (obj) => dispatch.auth.forgotPassword(obj),
  confirmationMessage: (obj) => dispatch.auth.confirmationMessage(obj),
  resetPassword: (obj) => dispatch.auth.resetPassword(obj),
  accountConfirmation: (obj) => dispatch.auth.accountConfirmation(obj),
  registerConfirmation: () => dispatch.auth.resendConfirmationEmail(),
  validatePassword: (obj) => dispatch.content.validatePassword(obj),
  getProvinces: (obj) => dispatch.content.getProvinces(obj),
  checkEmail: (obj) => dispatch.auth.checkEmail(obj),
  checkPhone: (obj) => dispatch.auth.checkPhone(obj),
  tillLogin: (obj) => dispatch.auth.tillLogin(obj),
  tradingLogin: (obj) => dispatch.auth.tradingLogin(obj),
  tillRecoverPassword: (obj) => dispatch.auth.tillRecoverPassword(obj),
  customerLogin: (obj) => dispatch.auth.userByAccount(obj),
  fica: (obj) => dispatch.peripherals.submitFicaForm(obj),
  retailRegister: (obj) => dispatch.auth.registerRetail(obj),
  changePassword: (obj) => dispatch.auth.changePassword(obj)
});

export default connect(mapState, mapDispatch)(withTranslation('common')(Form));
